import { gql, useQuery } from '@apollo/client';
import { BoxProps, Circle, HStack, Text } from '@chakra-ui/react';
import * as React from 'react';
import { useMentorshipContext } from '@/modules/mentorship/common/context/useMentorshipContext';

import { Footer, FooterButton, FooterButtonLabel } from '@/components/Footer';
import { ECommunityAccessConfig } from '@/graphql-types/globalTypes';
import {
    BookOpenIcon,
    HeadphonesIcon,
    HomeIcon,
    TagIcon,
    UsersIcon,
} from '@/imports/ui/chakra/feather';
import { VideoMeetIcon } from '@/imports/ui/chakra/feather/video-meet';
import { useFeatureFlags } from '@/imports/ui/hooks/useFeatureFlags';
import { useMeetingsLiveIndicators } from '@/modules/meetings/common/hooks/useMeetingsLiveIndicators';

import { VanillaMobileNavigation_Query } from './graphql-types/VanillaMobileNavigation_Query';

const QUERY = gql`
  query VanillaMobileNavigation_Query {
    currentUser {
      _id
      canAccessCommunity
      canAccessCommunityReadOnly
      mentorEnabled
      menteeEnabled
      visibleGroupLinks {
        _id
        userGroupRole
      }
    }
    tenant {
      result {
        _id
        communityAccessConfig
      }
    }
  }
`;

export const VanillaMobileNavigation: React.FC<BoxProps> = props => {
  const query = useQuery<VanillaMobileNavigation_Query>(QUERY);
  const { mentorshipEnabled } = useFeatureFlags();
  const { showMeetingsDot, showLiveLabel } = useMeetingsLiveIndicators({
    audienceOnly: true,
    skip: !query.data?.currentUser?._id,
  });

  const currentUser = query.data?.currentUser;
  const tenant = query.data?.tenant.result;
  const canAccessCommunity = currentUser
    ? currentUser.canAccessCommunity
    : tenant?.communityAccessConfig === ECommunityAccessConfig.PUBLIC;
  const canAccessCommunityReadOnly = currentUser?.canAccessCommunityReadOnly;
  const { context: mentorshipContext } = useMentorshipContext(Boolean(currentUser?._id && mentorshipEnabled));
  const canAccessMentorship =
    mentorshipEnabled &&
    (mentorshipContext?.mentorApplied || mentorshipContext?.menteeApplied || currentUser?.mentorEnabled === true || currentUser?.menteeEnabled === true);

  return (
    <Footer {...props}>
      {canAccessCommunity && (
        <FooterButton href="/community" isExact>
          <HomeIcon boxSize="5" />
          <FooterButtonLabel>Feed</FooterButtonLabel>
        </FooterButton>
      )}

      {canAccessCommunityReadOnly && (
        <FooterButton href="/my-company/community">
          <HeadphonesIcon boxSize="5" />
          <FooterButtonLabel>Community</FooterButtonLabel>
        </FooterButton>
      )}

      {canAccessCommunity && (
        <FooterButton href="/community/tags">
          <TagIcon boxSize="5" />
          <FooterButtonLabel>Tags</FooterButtonLabel>
        </FooterButton>
      )}

      {canAccessCommunity && (
        <FooterButton href="/community/quick-call/list">
          <VideoMeetIcon boxSize="5" />
          {showMeetingsDot && !showLiveLabel ? (
            <Circle
              position="absolute"
              top="2"
              right="5"
              size="2"
              bg="green.400"
            />
          ) : null}
          <FooterButtonLabel>
            <HStack as="span" spacing="1" justify="center">
              <Text as="span">Meetings</Text>
              {showLiveLabel ? (
                <Text
                  as="span"
                  fontSize="2xs"
                  fontWeight="bold"
                  color="green.500"
                  letterSpacing="wide"
                  textTransform="uppercase"
                >
                  LIVE
                </Text>
              ) : null}
            </HStack>
          </FooterButtonLabel>
        </FooterButton>
      )}

      {canAccessCommunity && (
        <FooterButton href="/community/groups">
          <BookOpenIcon boxSize="5" />
          <FooterButtonLabel>Groups</FooterButtonLabel>
        </FooterButton>
      )}

      {currentUser && canAccessMentorship && (
        <FooterButton href="/mentorship">
          <UsersIcon boxSize="5" />
          <FooterButtonLabel>Mentorship</FooterButtonLabel>
        </FooterButton>
      )}
    </Footer>
  );
};
